import { startInactiveSpan } from "@sentry/react";

// PRODUCTION ONLY: the DSN exists only in config/security.env.production, so dev/qa/stg
// have no DSN -> every function here is a no-op and the API clients behave exactly as
// before. This keeps non-prod byte-for-byte unchanged even though the clients run in all envs.
const enabled = Boolean(process.env.REACT_APP_SENTRY_DSN);

/**
 * Start a standalone `http.client` timing span for ONE API request.
 *
 * `forceTransaction: true` makes it a self-contained segment span that is recorded even when
 * no pageload/navigation transaction is active — which is the norm in this router-less iframe
 * embed once the boot transaction idles out, and the reason Sentry's automatic fetch/XHR
 * instrumentation misses the conversation APIs (it only records spans while a parent is active).
 *
 * The span name aggregates by API so you can sort by p75/p95 to find the slowest ones: for
 * REST/ML it is `METHOD host/path` (the path distinguishes each API); for GraphQL the operation
 * is appended (`METHOD host/graphql (operation)`) because every GraphQL op shares the same
 * `/graphql` endpoint and would otherwise collapse into a single group. Only PII-safe scalars
 * are attached — never the query string, request payload, or response body.
 *
 * Returns the span (to be passed to {@link endApiSpan}) or `null` when disabled / on failure.
 */
export const startApiSpan = ({ client, method, endpoint, operation } = {}) => {
  if (!enabled) {
    return null;
  }

  try {
    const attributes = { "api.client": client, "http.method": method, "http.endpoint": endpoint };

    if (operation) {
      attributes["graphql.operation"] = operation;
    }

    const base = `${method || "GET"} ${endpoint || "unknown"}`;

    return startInactiveSpan({
      name: operation ? `${base} (${operation})` : base,
      op: "http.client",
      forceTransaction: true,
      attributes,
    });
  } catch (_) {
    return null;
  }
};

/**
 * Finish a span started by {@link startApiSpan}, recording the HTTP status and, on failure,
 * an error span status (so Sentry's failure-rate/duration views count it). A request is
 * treated as failed when `errored` is set (network error / no response) or the status is >= 400.
 * No-op for a null span. Never throws — telemetry must not break the request.
 */
export const endApiSpan = (span, { status, errored } = {}) => {
  if (!span) {
    return;
  }

  try {
    if (typeof status === "number") {
      span.setAttribute("http.status_code", status);
    }

    if (errored || (typeof status === "number" && status >= 400)) {
      span.setStatus({ code: 2, message: "error" });
    }

    span.end();
  } catch (_) {
    // swallow — a telemetry failure must never affect the API call
  }
};
